import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { CircleDot, ThermometerIcon, Wifi, WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TitleField, ValueField } from '../../Resume/styles';
import {
  PortDetailsContainer,
  PortHeader,
  StatusBadge,
  ConnectedStatusBadgeText,
  DisconnectedStatusBadgeText,
  DataGridContainer,
  DataGridItem,
  DataGridLabel,
  DataGridValue,
} from '../Connections/styles';
import { Info } from '../../styles';

function PonDetails({
  selectedPort,
  isConnected,
  isEnabled,
}: {
  selectedPort: PonPortType;
  isConnected: boolean;
  isEnabled: boolean;
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  const name = selectedPort.name?.value || 'Port';
  const macAddress = selectedPort.macAddress?.value || null;

  const rx = Number(selectedPort.rx?.value ?? '0');
  const tx = Number(selectedPort.tx?.value ?? '0');
  const temperature = Number(selectedPort.temperature?.value ?? '0');
  const bytesSent = Number(selectedPort.bytesSent?.value ?? '0');
  const bytesReceived = Number(selectedPort.bytesReceived?.value ?? '0');

  return (
    <PortDetailsContainer>
      {/* Header with port information */}
      <PortHeader>
        <Stack direction="row" alignItems="flex-start" gap={1.5}>
          <CircleDot size={28} color={theme.palette.primary['06']} />
          <Stack alignItems="flex-start">
            <Typography variant="h6" sx={{ fontWeight: 700 }} textAlign="left">
              {String(name)}
            </Typography>
            {macAddress && (
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.primary, fontWeight: 300 }}
              >
                {String(macAddress).toUpperCase()}
              </Typography>
            )}
          </Stack>
        </Stack>

        <Stack>
          <Info sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ThermometerIcon size={42} />
            <TitleField>{t('equipment.temperature')}</TitleField>
            <ValueField>{temperature}°C</ValueField>
          </Info>
        </Stack>

        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          sx={{ flexWrap: 'wrap' }}
        >
          <StatusBadge status={selectedPort.status?.value} enabled={isEnabled}>
            {isConnected ? (
              <>
                <Wifi size={16} color={theme.palette.fixed.white} />
                <ConnectedStatusBadgeText
                  variant="caption"
                  color={theme.palette.fixed.white}
                >
                  {t(`equipment.status.CONNECTED`)}
                </ConnectedStatusBadgeText>
              </>
            ) : (
              <>
                <WifiOff size={16} color={theme.palette.fixed.white} />
                <DisconnectedStatusBadgeText
                  variant="caption"
                  color={theme.palette.fixed.white}
                >
                  {t(`equipment.status.DISCONNECTED`)}
                </DisconnectedStatusBadgeText>
              </>
            )}
          </StatusBadge>
        </Stack>
      </PortHeader>

      {/* Statistics Grid */}
      <DataGridContainer>
        <DataGridItem>
          <DataGridLabel>{t('equipment.transmitionRate')}</DataGridLabel>
          <DataGridValue>{tx} dBm</DataGridValue>
        </DataGridItem>
        <DataGridItem>
          <DataGridLabel>{t('equipment.receiveRate')}</DataGridLabel>
          <DataGridValue>{rx} dBm</DataGridValue>
        </DataGridItem>
        <DataGridItem>
          <DataGridLabel>{t('equipment.wan.bytesSent')}</DataGridLabel>
          <DataGridValue>{bytesSent} GB</DataGridValue>
        </DataGridItem>
        <DataGridItem>
          <DataGridLabel>{t('equipment.wan.bytesReceived')}</DataGridLabel>
          <DataGridValue>{bytesReceived} GB</DataGridValue>
        </DataGridItem>
      </DataGridContainer>
    </PortDetailsContainer>
  );
}

export default PonDetails;
